export type Vec2 = readonly [number, number]
export type Vec3 = readonly [number, number, number]

/**
 * The Gray-Scott half: what the field does.
 *
 * The f/k pairs are Robert Munafo's catalogue from the xmorphia explorer
 * (https://mrob.com/pub/comp/xmorphia/), by way of Jason Webb's
 * reaction-diffusion-playground. They are extremely sensitive — a change of
 * 0.001 in either can be the difference between a living pattern and a field
 * that dies out or saturates.
 */
export interface Chemistry {
  /** feed rate */
  f: number
  /** kill rate */
  k: number
  /** diffusion rate of A */
  dA: number
  /** diffusion rate of B */
  dB: number
  /** integration step; above ~1.2 the explicit solver goes unstable */
  dt: number
  /** simulation steps per displayed frame — the pattern's speed */
  steps: number
  /** peak-to-peak spread of f and k across the field */
  spread: Vec2
  /** 0 = uniform parameters everywhere, 1 = full spread */
  variance: number
  /** spatial frequency of the parameter field */
  varianceScale: number
  /**
   * How fast the parameter field morphs, in noise units per second. A full
   * unit decorrelates it, so 0.12 is a complete change of local conditions
   * roughly every eight seconds.
   */
  evolve: number
  /** base fall speed, in simulation texels per step */
  drift: number
  /** how much the fall rate varies across the field, 0..1 */
  flowVary: number
  /** sideways component of the flow, as a fraction of the fall speed */
  flowLateral: number
  /** spatial frequency of the flow field; low values mean broad currents */
  flowScale: number
  /** how fast the flow field itself changes */
  flowEvolve: number
  /**
   * How hard the mark is held each step, 0..1. Low values let diffusion
   * smooth its edges into the field; high values re-stamp a hard raster and
   * stair-step at grid resolution.
   */
  markPin: number
  /**
   * Concentration the mark is held at. Should match what the pattern's own
   * structures reach, or the mark shades as a different material.
   */
  markLevel: number
  /** steady-state strength of the source along the top edge; 0 disables it */
  emit: number
  /**
   * Strength that source starts at. The field opens empty and is filled from
   * above, then this decays to `emit` over `emitFade` seconds. Seeding the
   * whole field instead scatters the pattern everywhere at once; seeding only
   * the top would not work, because this regime does not invade empty space
   * and the field wraps vertically — a band would simply circulate.
   */
  emitStart: number
  /** seconds over which emitStart decays to emit */
  emitFade: number
  /** height of that source band, in uv */
  emitBand: number
  /** horizontal frequency of its variation */
  emitScale: number
  /** how fast its pattern changes */
  emitSpeed: number
  /**
   * Threshold the seed noise is cut at, so LOWER means more of the field
   * starts occupied and the pattern has less empty ground to invade. Too low
   * and B consumes A everywhere at once and the field collapses to nothing.
   */
  seedDensity: number
  /** scales the whole reset seed; 0 opens on an empty field */
  seedAmount: number
}

/**
 * The shading half: what the field looks like. All colours are linear, not
 * sRGB — the display pass tonemaps and applies the transfer curve at the end.
 */
export interface Material {
  matteAlbedo: Vec3
  matteRough: number
  glossAlbedo: Vec3
  glossRough: number
  /** normal-incidence reflectance; 0.04–0.05 for dielectrics */
  f0: number
  /** B values mapped across the matte→gloss transition */
  edge: Vec2
  /** how strongly the B gradient bends the surface normal */
  relief: number
  /** micro-normal jitter on the matte, so charcoal reads as charcoal */
  microGrain: number

  ambient: Vec3
  keyDir: Vec3
  keyColor: Vec3
  keySize: Vec2
  fillDir: Vec3
  fillColor: Vec3
  fillSize: Vec2

  /** colour of the distant light on the right, and on the left */
  rimWarm: Vec3
  rimCool: Vec3
  /** overall strength of the pair; 0 disables them */
  rimGain: number
  /** how sharply the wash favours tilted surfaces; higher = tighter rim */
  rimFocus: number
  /** how much of the warm/cool split follows screen position, 0..1 */
  rimSpread: number

  /** strength of the slow large-scale surface swell */
  swell: number
  /** its spatial frequency; low values mean broad, lazy curves */
  swellScale: number
  swellSpeed: number

  exposure: number
  vignette: number
  /** output dither; dark gradients band without it */
  grain: number
}

export interface Preset {
  id: string
  name: string
  chemistry: Chemistry
  material: Material
}

const CHEMISTRY_DEFAULTS = {
  dA: 1.0,
  dB: 0.5,
  dt: 1.0,
  steps: 12,
  spread: [0.010, 0.006] as Vec2,
  variance: 1.0,
  varianceScale: 2.2,
  evolve: 0.12,
  drift: 0.021,
  flowVary: 1.0,
  flowLateral: 1.03,
  flowScale: 1.0,
  flowEvolve: 0.3,
  markPin: 0.013,
  markLevel: 0.57,
  emit: 0.0,
  emitStart: 0.0,
  emitFade: 60.0,
  emitBand: 0.03,
  emitScale: 30.0,
  emitSpeed: 0.14,
  seedDensity: 0.46,
  seedAmount: 1.0,
} satisfies Omit<Chemistry, 'f' | 'k'>

/** Charcoal matte substrate, piano-black pattern, two-softbox studio. */
export const CHARCOAL_GLOSS: Material = {
  matteAlbedo: [0.200, 0.202, 0.210],
  matteRough: 0.94,
  glossAlbedo: [0.006, 0.006, 0.009],
  glossRough: 0.055,
  f0: 0.045,
  edge: [0.05, 0.17],
  relief: 5.0,
  microGrain: 0.16,

  ambient: [0.175, 0.178, 0.188],
  keyDir: [-0.42, 0.72, 0.55],
  keyColor: [2.6, 2.58, 2.5],
  keySize: [0.62, 0.10],
  fillDir: [0.68, -0.34, 0.65],
  fillColor: [0.40, 0.42, 0.47],
  fillSize: [0.34, 0.30],

  rimWarm: [0.300, 0.042, 0.212],
  rimCool: [0.021, 0.150, 0.084],
  rimGain: 1.42,
  rimFocus: 0.4,
  rimSpread: 1.0,

  swell: 0.14,
  swellScale: 3.0,
  swellSpeed: 0.35,

  exposure: 1.0,
  vignette: 0.54,
  grain: 0.006,
}

function preset(id: string, name: string, f: number, k: number, chemistry: Partial<Chemistry> = {}, material: Partial<Material> = {}): Preset {
  return {
    id,
    name,
    chemistry: { ...CHEMISTRY_DEFAULTS, f, k, ...chemistry },
    material: { ...CHARCOAL_GLOSS, ...material },
  }
}

export const PRESETS: readonly Preset[] = [
  // Grows outward from whatever is seeded, which is what makes an image
  // seed spread across the field rather than sit still.
  preset('coral', 'Coral', 0.0545, 0.062, {
    steps: 12,
    variance: 0.35,
  }),
  preset('mitosis', 'Mitosis', 0.0367, 0.0649, {
    steps: 12,
    variance: 0.3,
  }),
  preset('replicating', 'Self-replicating spots', 0.03, 0.063, {
    steps: 12,
    variance: 0.3,
  }),

  // Slow, dense ridges. The most "material" of the set — reads as machined
  // black inlay in charcoal, which is why it is the default.
  preset('fingerprints', 'Fingerprints', 0.037, 0.06, {
    steps: 10,
    variance: 0.45,
  }),
  preset('mazes', 'Mazes', 0.029, 0.057, {
    steps: 10,
    variance: 0.4,
  }),
  preset('worms', 'Worms', 0.058, 0.065, {
    steps: 14,
    // Full variance pushes swathes of the field past the edge of the living
    // regime, where the pattern dies and never comes back — which reads as
    // bare ground rather than as variety. Enough to keep the worms from
    // looking machine-uniform, not enough to kill them.
    variance: 0.35,
  }),
  preset('worms-loops', 'Worms and loops', 0.082, 0.06, {
    steps: 14,
    variance: 0.5,
  }),
  preset('solitons', 'Stable solitons', 0.074, 0.064, {
    steps: 16,
    variance: 0.35,
    seedDensity: 0.62,
  }, {
    edge: [0.12, 0.30],
    relief: 7.0,
  }),
  preset('u-skate', 'U-Skate World', 0.062, 0.0609, {
    steps: 18,
    variance: 0.3,
    seedDensity: 0.6,
  }),
  preset('spots-worms', 'Spots and worms', 0.034, 0.0618, {
    steps: 12,
    variance: 0.5,
  }),
  preset('pulsating', 'Pulsating solitons', 0.025, 0.06, {
    steps: 16,
    variance: 0.3,
  }),
  preset('waves', 'Waves', 0.014, 0.045, {
    steps: 20,
    variance: 0.25,
    seedDensity: 0.5,
  }, {
    edge: [0.10, 0.28],
    relief: 4.0,
  }),
  preset('chaos', 'Chaos', 0.026, 0.051, {
    steps: 14,
    variance: 0.45,
  }),
]

export const DEFAULT_PRESET: Preset =
  PRESETS.find((p) => p.id === 'worms') ?? (PRESETS[0] as Preset)

export function presetById(id: string): Preset {
  return PRESETS.find((p) => p.id === id) ?? DEFAULT_PRESET
}
